/**
 * TitleEditor Component
 * Modal for editing chart titles with AI-powered variations
 * Features:
 * - Free-form text editing
 * - AI-generated variations (5 options)
 * - Reset to auto-generated
 * - Preview before applying
 */

import React, { useState } from 'react';
import { X, Sparkles, RotateCcw, Save } from 'lucide-react';

export interface TitleEditorProps {
  isOpen: boolean;
  onClose: () => void;
  currentTitle: string;
  currentSubtitle: string;
  onSave: (title: string, subtitle: string) => void;
  onReset: () => void;
  chartContext?: {
    chartType?: string;
    timeframe?: string;
    isMA?: boolean;
    totalValue?: number;
    growthRate?: number;
    topCategory?: string;
  };
}

interface TitleVariation {
  title: string;
  subtitle: string;
}

interface AITitleResponse {
  success: boolean;
  variations?: TitleVariation[];
  error?: string;
  timestamp: string;
}

export const TitleEditor: React.FC<TitleEditorProps> = ({
  isOpen,
  onClose,
  currentTitle,
  currentSubtitle,
  onSave,
  onReset,
  chartContext
}) => {
  const [title, setTitle] = useState(currentTitle);
  const [subtitle, setSubtitle] = useState(currentSubtitle);
  const [variations, setVariations] = useState<TitleVariation[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVariationIndex, setSelectedVariationIndex] = useState<number | null>(null);

  // Reset form when modal opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setTitle(currentTitle);
      setSubtitle(currentSubtitle);
      setVariations([]);
      setSelectedVariationIndex(null);
      setError(null);
    }
  }, [isOpen, currentTitle, currentSubtitle]);

  /**
   * Generate AI variations using Claude API
   */
  const handleGenerateVariations = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const apiUrl = import.meta.env.VITE_API_ENDPOINT || window.location.origin;
      const response = await fetch(`${apiUrl}/api/v2/ai/title-variations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currentTitle: title,
          currentSubtitle: subtitle,
          ...chartContext
        })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json() as AITitleResponse;

      if (data.success && data.variations) {
        setVariations(data.variations);
        setSelectedVariationIndex(null); // Reset selection
      } else {
        throw new Error(data.error || 'Failed to generate variations');
      }
    } catch (err) {
      console.error('Error generating variations:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate variations');
    } finally {
      setIsGenerating(false);
    }
  };

  /**
   * Select a variation
   */
  const handleSelectVariation = (index: number) => {
    setSelectedVariationIndex(index);
    setTitle(variations[index].title);
    setSubtitle(variations[index].subtitle);
  };

  /**
   * Save custom title
   */
  const handleSave = () => {
    if (title.trim() && subtitle.trim()) {
      onSave(title.trim(), subtitle.trim());
      onClose();
    }
  };

  /**
   * Reset to auto-generated
   */
  const handleReset = () => {
    onReset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden pointer-events-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Edit Chart Title
            </h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
            {/* Manual Edit Section */}
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Enter chart title..."
                  maxLength={60}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {title.length}/60 characters
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Subtitle
                </label>
                <input
                  type="text"
                  value={subtitle}
                  onChange={(e) => setSubtitle(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Enter subtitle..."
                  maxLength={80}
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  {subtitle.length}/80 characters
                </p>
              </div>
            </div>

            {/* AI Variations Section */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  AI Variations
                </h3>
                <button
                  onClick={handleGenerateVariations}
                  disabled={isGenerating}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <Sparkles className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                  {isGenerating ? 'Generating...' : 'Generate 5 Options'}
                </button>
              </div>

              {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {variations.length > 0 && (
                <div className="space-y-3">
                  {variations.map((variation, index) => (
                    <button
                      key={index}
                      onClick={() => handleSelectVariation(index)}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        selectedVariationIndex === index
                          ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-purple-300 dark:hover:border-purple-700'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-white truncate">
                            {variation.title}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {variation.subtitle}
                          </p>
                        </div>
                        {selectedVariationIndex === index && (
                          <div className="flex-shrink-0">
                            <div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs">✓</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {variations.length === 0 && !isGenerating && !error && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">
                    Click "Generate 5 Options" to see AI-powered title variations
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            <button
              onClick={handleReset}
              className="flex items-center gap-2 px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to Auto
            </button>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={!title.trim() || !subtitle.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Save className="w-4 h-4" />
                Save Custom Title
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TitleEditor;
